import { effect, signal, WritableSignal } from '@angular/core';

/** A signal that restores from and writes back to localStorage; `isValid` rejects stale or foreign values. */
export function storedSignal<T>(
  key: string,
  fallback: T,
  isValid: (value: unknown) => value is T = (value): value is T => value !== undefined
): WritableSignal<T> {
  const state = signal<T>(read(key, fallback, isValid));
  effect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state()));
    } catch {
      return;
    }
  });
  return state;
}

function read<T>(key: string, fallback: T, isValid: (value: unknown) => value is T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const value: unknown = JSON.parse(raw);
    return isValid(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

export const isOneOf =
  <T extends string>(values: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === 'string' && (values as readonly string[]).includes(value);

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string');

export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';
